import { ref, computed } from "vue";
import router from "@/app/router";
import { defineStore } from "pinia";
import {
        EnumEmploymentType,
        EnumDocumentationSendType,
        EnumPersonalDocumentType,
        EnumIdentificationType,
        EnumCollectionPointType,
        EnumPasswordStatus,
        EnumFlowType,
        IProduct,
        EnumContractSignatureType,
        EnumContractSending,
        useUtilities
    } from "nlb-buy-go-library";
import { IKeyValue } from "./app-store";
import { IFileData } from "fw4-library";
import { commClient } from "fw4-library";

interface IContractCreateStore {
    ProcessData: IProcessData;
    WebOrderData: IWebOrderData;
    CustomerData: ICustomerData;
    EmploymentData: IEmploymentData;
    ContractData: IContractData;
    CalculationData: ICalculationData;
    PreapprovedData: IPreapproved;
    ExposureData: IExposureData;
    IdentificationData: IIdentificationData;
    SisbonData: ISisbonData;
    HeaderData: IHeaderData;
    ContractSuccessData: IContractSuccess;
    PasswordResetData: IPasswordResetData;
};

interface IWebOrderData {
    orderToken: string;
    referenceNumber: string;
    successPage: string;
    errorPage: string;
    orderId: string;
    minInstallment: number;
    maxInstallment: number;
    logoImage: string;
    storeUrl: string;
    storeName: string;
    hasPersonalPickUp: boolean;
    hasHomeDelivery: boolean;
    hasMultipleProducts: boolean;
}

interface ISisbonData {
    sisbonResultId: number;
    debtAmount: number;
    debtInstallments: number;
    minSalary: number;
    minCredit: number;
};

interface IHeaderData {
    headerTitle: string;
    headerSubtitle: string;
};

interface IContractSuccess {
    successText: string;
};

interface ICustomerData {
    prefix: IKeyValue | undefined;
    phoneNumber: number | null;
    msisdn: string | undefined;
    email: string | undefined;
    isEmailVerified: boolean;
    taxNumber: number| undefined;
    firstname: string;
    surname: string;
    emso: string | null;
    address: string;
    postalCode: number | undefined;
    isMale: boolean;
    birthdate: Date | null;
    birthCity: string;
    citizen: number | undefined;
    hasTemporaryResidence: boolean | undefined;
    temporaryCitizen: number | undefined;
    temporaryPostalCode: string | undefined;
    temporaryForeignPostalCode: string;
    temporaryAddress: string;
    countrySourceFunds: string;
    countryFundsStatus: string;
};

interface ICalculationData {
    calculationId: number;
    creditAmount: number | undefined;
    installmentNr: number;
    installmentVal: number;
    installmentValNoCost: number;
    lastMonthsIncome: number | undefined;
    lastMonthsIncome2: number | undefined;
    lastMonthsIncome3: number | undefined;
    sumInstallments: number;
    isDocumentationNeeded: boolean;
    mustProvideThreeSalaries: boolean;
};

interface IProcessData {
    flowType: EnumFlowType | undefined;
    isAppDataLoading: boolean;
    isViewDataLoading: boolean;
    loaderType: string;
    userFullName: string;
    isVehiclesFinancing: boolean;
    needsToChangePassword: boolean;
    isExistingUser: boolean;
    linkPasswordReset: string;
    linkParamsId: string;
    partnerId: number;
    isFromPasswordReset: boolean;
    isDataChecked: boolean;
};

interface IEmploymentData {
    employmentType: EnumEmploymentType | undefined;
    employerName: string;
    employerAddress: string;
    employerPostCode: string;
    isPoliticallyExposedPerson: boolean | undefined;
    politicalFunction: string;
    pioSourceFunds: string;
    pioFundsStatus: string;
};

interface IExposureData {
    minSalary: number;
    summitDebt: number;
    contractDebt: number;
    contractDebtInstallments: number;
    maxHitri: number | undefined;
    maxTriglavAmount: number | undefined;
};

interface IIdentificationData {
    isIdentified: boolean;
    personalDocumentType: EnumPersonalDocumentType | undefined;
    idNumber: string;
    idNumberCheck: string;
    documentIssuer: string;
    issueDate: Date | null;
    validityDate: Date | null;
    permanentValidity: boolean | undefined;
    identificationConfirmation: Boolean | undefined;
}

interface IContractData {
    hasPermanentResidenceInSlovenia : boolean | undefined;
    isSlovenianCitizen: boolean | undefined;
    hasSlovenianIncome: boolean | undefined;
    hasSlovenianBankAccount: boolean | undefined;
    creditAmount: number | undefined;
    pogodbaId: number;
    stPogodbe: string;
    triglavCode: string;
    documentationSendType: EnumDocumentationSendType | undefined;
    TRR: string;
    promoCode: string | undefined;
    code: string;
    selectedSpecialProduct: IProduct | undefined;
    selectedPromoProduct: IProduct | undefined;
    selectedProduct: IProduct | undefined;    
    selectedProductType: string | undefined;    
    documentNumber: string;
    willAttachDocumentation: boolean | undefined;
    bindingFiles: IFileData[];
    documentationFiles: IFileData[],
    termsAgreement: Boolean;
    isCompanyTaxNumber: boolean;
    productList: IProduct[];
    specialProductList: IProduct[];
    promoProductList: IProduct[];
    proformaFile: IFileData[];
    proformaPartnerType: string;
    dataConfirmation: boolean;
    isMarketingPreselected: boolean;
    marketingSummit: boolean;
    marketingCreditBroker: boolean;
    password: string;
    firstPassword: string;
    repeatPassword: string;
    status: EnumPasswordStatus;
    descriptionText: string;
    contractSignatureType: EnumContractSignatureType | undefined;
    contractSending: EnumContractSending | undefined;
    showEmploymentType: boolean;
    multipleContractAgreement: boolean;
}

interface IPreapproved {
    isPreapproved: boolean;
    numberOfPreapprovedSalaries: number;
    preapprovedCreditAmount: number;
    preapprovedInstallmentAmount: number;
}

interface IDemoData {
    propText: string;
    propText123: string;
    propNumber: number;
}

interface IPasswordResetData {
    taxNumber: string | undefined;
}

const defaultHeaderData: IHeaderData = {
    headerTitle: "",
    headerSubtitle: ""
};

const defaultContractSuccess: IContractSuccess = {
    successText: ""
};

const defaultProcessData: IProcessData = {
    flowType: undefined,
    isAppDataLoading: false,
    isViewDataLoading: false,
    loaderType: "",
    userFullName: "",
    isVehiclesFinancing: false,
    needsToChangePassword: false,
    isExistingUser: false,
    linkPasswordReset: 'Login',
    linkParamsId: '1',
    partnerId: -1,
    isFromPasswordReset: false,
    isDataChecked: false
}

const defaultWebOrderData: IWebOrderData = {
    orderToken: "",
    referenceNumber: "",
    successPage: "",
    errorPage: "",
    orderId: "",
    minInstallment: 0,
    maxInstallment: 0,
    logoImage: "",
    storeUrl: "",
    storeName: "",
    hasPersonalPickUp: true,
    hasHomeDelivery: true,
    hasMultipleProducts: false
}

const defaultEmploymentData: IEmploymentData = {
    employmentType: undefined,
    employerName: "",
    employerAddress: "",
    employerPostCode: "",
    isPoliticallyExposedPerson: undefined,
    politicalFunction: "",
    pioSourceFunds: "",
    pioFundsStatus: "",
}

const defaultCustomerData: ICustomerData = {
    prefix: { Key: "386", Value: "SI +386" },
    phoneNumber: null,
    email: "",
    msisdn: undefined,
    taxNumber: undefined,
    firstname: "",
    surname: "",
    emso:   null,
    address: "",
    postalCode: undefined,
    isMale: false,
    birthdate: null,
    birthCity: "",
    citizen: undefined,
    hasTemporaryResidence: undefined,
    temporaryCitizen: undefined,
    temporaryPostalCode: undefined,
    temporaryForeignPostalCode: "",
    temporaryAddress: "",
    countryFundsStatus: "",
    countrySourceFunds: "",
    isEmailVerified: false
}

const defaultIdentificationData: IIdentificationData = {
    isIdentified: false,
    personalDocumentType: undefined,
    idNumber: "",
    idNumberCheck: "",
    documentIssuer: "",
    issueDate: null,
    validityDate: null,
    permanentValidity: undefined,
    identificationConfirmation: false,
}

const defaultContractData: IContractData = {
    hasPermanentResidenceInSlovenia: undefined,
    isSlovenianCitizen: undefined,
    hasSlovenianIncome: undefined,
    hasSlovenianBankAccount: undefined,
    code: "",
    documentationSendType: undefined,
    TRR: "",
    creditAmount: undefined,
    selectedSpecialProduct: undefined,
    selectedPromoProduct: undefined,
    selectedProduct: undefined,
    selectedProductType: undefined,
    documentNumber: "",
    bindingFiles: [],
    willAttachDocumentation: undefined,
    documentationFiles: [],
    termsAgreement: false,
    isCompanyTaxNumber: false,
    proformaFile: [],
    proformaPartnerType: "",
    productList: [],
    specialProductList: [],
    promoProductList: [],
    dataConfirmation: false,
    marketingSummit: false,
    isMarketingPreselected: false,
    marketingCreditBroker: false,
    password: "",
    firstPassword: "",
    repeatPassword: "",
    status: EnumPasswordStatus.Success,
    descriptionText: "Geslo je bilo uspešno zamenjano",
    contractSignatureType: undefined,
    contractSending: undefined,
    promoCode: "",
    triglavCode: "",
    pogodbaId: 0,
    stPogodbe: "",
    showEmploymentType: false,
    multipleContractAgreement: false
}

const defaultCalculationData: ICalculationData = {
    calculationId: 0,
    creditAmount: undefined,
    installmentNr: 0,
    installmentVal: 0,
    installmentValNoCost: 0,
    lastMonthsIncome: undefined,
    lastMonthsIncome2: undefined,
    lastMonthsIncome3: undefined,
    sumInstallments: 0,
    mustProvideThreeSalaries: false,
    isDocumentationNeeded: false
}

const defaultPreapprovedData: IPreapproved = {
    isPreapproved: false,
    numberOfPreapprovedSalaries: 0,
    preapprovedCreditAmount: 0,
    preapprovedInstallmentAmount: 0
}

const defaultExposureData: IExposureData = {
    minSalary: 0,
    summitDebt: 0,
    contractDebt: 0,
    contractDebtInstallments: 0,
    maxHitri: undefined,
    maxTriglavAmount: undefined
}

const defaultSisbonData: ISisbonData = {
    sisbonResultId: -1,
    debtAmount: 0,
    debtInstallments: 0,
    minSalary: 0,
    minCredit: 0
}

const defaultDemoData: IDemoData = {
    propText: "init from store",
    propText123: "init from store 123",
    propNumber: 3
}

const defaultPasswordResetData: IPasswordResetData = {
    taxNumber: undefined,
}

export const useContractCreateStore = defineStore("contractCreateStore", () => {
    const AppState = ref<IContractCreateStore>({
        ProcessData: {
            ...defaultProcessData
        },
        WebOrderData: {
            ...defaultWebOrderData
        },
        CustomerData: {
            ...defaultCustomerData
        },
        EmploymentData: {
            ...defaultEmploymentData
        },
        ContractData: {
            ...defaultContractData
        },
        CalculationData: {
            ...defaultCalculationData
        },
        PreapprovedData: {
            ...defaultPreapprovedData
        },
        IdentificationData: {
            ...defaultIdentificationData
        },
        ExposureData: {
            ...defaultExposureData
        },
        SisbonData: {
            ...defaultSisbonData
        },
        HeaderData: {
            ...defaultHeaderData
        },
        ContractSuccessData: {
            ...defaultContractSuccess
        },
        PasswordResetData: {
            ...defaultPasswordResetData
        }
    });

    const { copySharedProperties  } = useUtilities();

    async function loadOrderData(orderToken: string) {
        AppState.value.WebOrderData.orderToken = orderToken;

        return new Promise<any>((resolve) => {
            commClient.executeMethodAsync("sw.WebOrderDataLoadNewWeb",{
                OrderToken: AppState.value.WebOrderData.orderToken
            }).then(async (Message: any) => {
                if (Message) {
                    if(Message.Success){
                        copySharedProperties(AppState.value.ProcessData, Message);
                        copySharedProperties(AppState.value.WebOrderData, Message);
                        copySharedProperties(AppState.value.CustomerData, Message);
                        copySharedProperties(AppState.value.ContractData, Message);
                        copySharedProperties(AppState.value.ExposureData, Message);
                        copySharedProperties(AppState.value.SisbonData, Message);
                        copySharedProperties(AppState.value.CalculationData, Message);
                        copySharedProperties(AppState.value.IdentificationData, Message);

                        if (AppState.value.ProcessData.flowType === EnumFlowType.POS_STRANKA) {
                            AppState.value.IdentificationData.isIdentified = true;
                        }

                        if(AppState.value.ProcessData.flowType.startsWith("diy") || Message.isFromVerifiedEmail === true || AppState.value.ProcessData.isFromPasswordReset) {
                            if(AppState.value.ProcessData.isFromPasswordReset){
                                AppState.value.ProcessData.isExistingUser = true;
                            }
                            // AppState.value.CustomerData.prefix = { Key: Message.prefixValue, Value: "+" + Message.prefixValue};
                            router.push({ name: "Login", params: {id: orderToken}});
                        } else {
                            router.push({name: "VerificationPhone", params: {id: orderToken}});
                        }
                    }
                    else{
                        AppState.value.HeaderData.headerSubtitle = `Pogodba z naročilom ${orderToken} že obstaja.`;
                        router.push({name: "EndFail", params: { id: orderToken } });
                    }
                }
                else{
                    AppState.value.HeaderData.headerSubtitle = `Ne najdemo vašega naročila ${orderToken}.`;
                    router.push({name: "EndFail", params: { id: orderToken } });
                }                
            });
        })
    };

    function reset() {
        AppState.value.ProcessData = { ...defaultProcessData };
        AppState.value.WebOrderData = { ...defaultWebOrderData };
        AppState.value.CustomerData = { ...defaultCustomerData };
        AppState.value.EmploymentData = { ...defaultEmploymentData };
        AppState.value.ContractData = { ...defaultContractData };
        AppState.value.CalculationData = { ...defaultCalculationData };
        AppState.value.PreapprovedData = { ...defaultPreapprovedData };
        AppState.value.IdentificationData = { ...defaultIdentificationData };
        AppState.value.ExposureData = { ...defaultExposureData };
        AppState.value.SisbonData = { ...defaultSisbonData };
        AppState.value.HeaderData = { ...defaultHeaderData };
        AppState.value.ContractSuccessData = { ...defaultContractSuccess };
        AppState.value.PasswordResetData = { ...defaultPasswordResetData };
    };

    const isPreapprovedValid = computed(() => {
        return (
            AppState.value.PreapprovedData.isPreapproved === true &&
            AppState.value.PreapprovedData.preapprovedCreditAmount >= AppState.value.ContractData.creditAmount! &&
            AppState.value.PreapprovedData.preapprovedInstallmentAmount >= AppState.value.CalculationData.installmentValNoCost
        );
    });
    // const AppState = ref<IContractCreateStore>({
    //     ProcessData: {
    //         ...defaultProcessDataForTesting
    //     },
    //     CustomerData: {
    //         ...defaultCustomerDataForTesting
    //     },
    //     EmploymentData: {
    //         ...defaultEmploymentDataForTesting
    //     },
    //     ContractData: {
    //         ...defaultContractDataForTesting
    //     },
    //     CalculationData: {
    //         ...defaultCalculationDataForTesting
    //     },
    //     PreapprovedData: {
    //         ...defaultPreapprovedData
    //     },
    //     ExposureData: {
    //         ...defaultExposureData
    //     },
    //     DemoData: {
    //         ...defaultDemoData
    //     },
    // });

    // function reset() {
    //     AppState.value.ProcessData = { ...defaultProcessDataForTesting }
    //     AppState.value.CustomerData = { ...defaultCustomerDataForTesting }
    //     AppState.value.EmploymentData = { ...defaultEmploymentDataForTesting }
    //     AppState.value.ContractData = { ...defaultContractDataForTesting }
    //     AppState.value.CalculationData = { ...defaultCalculationDataForTesting }
    //     AppState.value.PreapprovedData = { ...defaultPreapprovedData }
    //     AppState.value.ExposureData = { ...defaultExposureData }
    //     AppState.value.DemoData = { ...defaultDemoData }
    // }
    
    return {
        AppState,
        reset,
        loadOrderData,
        isPreapprovedValid
    };
});


const defaultProcessDataForTesting: IProcessData = {
    flowType: "pos",
    isAppDataLoading: false,
    isViewDataLoading: false,
    loaderType: "",
    userFullName: "",
    isVehiclesFinancing: false,
    needsToChangePassword: false,
    isExistingUser: false,
    linkPasswordReset: 'login',
    linkParamsId: ':1',
    partnerId: -1,
    isFromPasswordReset: false,
    isDataChecked: false
};

const defaultCalculationDataForTesting: ICalculationData = {
    calculationId: 763411,
    creditAmount: 1123,
    installmentNr: 15,
    installmentVal: 123,
    installmentValNoCost: 123,
    lastMonthsIncome: 1234,
    lastMonthsIncome2: 1234,
    lastMonthsIncome3: 1234,
    sumInstallments: 0,
    mustProvideThreeSalaries: false,
    isDocumentationNeeded: false
};

const defaultCustomerDataForTesting: ICustomerData = {
    prefix: {
        Key: "386",
        Value: "SI +386"
    },
    phoneNumber: 41795886,
    msisdn: "38641795886",
    email: "simong@hgi.si",
    taxNumber: 18911331,
    firstname: "SIMON",
    surname: "TEST",
    emso:   "0806976500479",
    address: "Pot k studencu 12",
    postalCode: 1000,
    isMale: false,
    birthdate: null,
    birthCity: "Ljubljana",
    citizen: 204,
    hasTemporaryResidence: false,
    temporaryCitizen: undefined,
    temporaryPostalCode: undefined,
    temporaryForeignPostalCode: "",
    temporaryAddress: "",
    countryFundsStatus: "",
    countrySourceFunds: "",
    isEmailVerified: false
};

const defaultEmploymentDataForTesting: IEmploymentData = {
    employmentType: EnumEmploymentType.Zaposlen,
    employerName: "",
    employerAddress: "",
    employerPostCode: "",
    isPoliticallyExposedPerson: false,
    politicalFunction: "",
    pioSourceFunds: "",
    pioFundsStatus: "",
}

const defaultIdentificationDataForTesting: IIdentificationData = {
    isIdentified: false,
    personalDocumentType: "OS",
    idNumber: "123456789",
    idNumberCheck: "123456789",
    documentIssuer: "LJUBLJANA",
    issueDate: new Date(2020, 1, 1),
    validityDate: new Date(2030, 1, 1),
    permanentValidity: undefined,
    identificationConfirmation: false,
};

const defaultContractDataForTesting: IContractData = {
    hasPermanentResidenceInSlovenia: true,
    isSlovenianCitizen: true,
    hasSlovenianIncome: true,
    hasSlovenianBankAccount: true,

    documentationSendType: undefined,

    TRR: "051007041274011",

    creditAmount: 1123,

    // employmentType: undefined,
    selectedSpecialProduct: undefined,
    selectedPromoProduct: undefined,
    selectedProductType: "OBR",
    selectedProduct: {
        productId: "OBR",
        productName: "Obrestni kredit",
        installmentMin: 3,
        installmentMax: 84,
        amountMin: 120,
        amountMax: 15000,
        ageMin: 18,
        ageMax: 80,
    },
    code: "",
    documentNumber: "123456789",
    bindingFiles: [],
    willAttachDocumentation: false,
    documentationFiles: [],
    termsAgreement: false,
    isCompanyTaxNumber: false,
    proformaFile: [],
    proformaPartnerType: "",
    productList: [],
    specialProductList: [],
    promoProductList: [],

    dataConfirmation: false,
    marketingSummit: false,
    isMarketingPreselected: false,
    marketingCreditBroker: false,

    password: "",
    firstPassword: "",
    repeatPassword: "",
    status: EnumPasswordStatus.Success,
    descriptionText: "Geslo je bilo uspešno zamenjano",
    contractSignatureType: undefined,
    contractSending: undefined,
    promoCode: "",
    triglavCode: "",
    pogodbaId: 0,
    stPogodbe: "",
    showEmploymentType: false,
    multipleContractAgreement: false
}